import React from 'react';
import { motion } from 'framer-motion';
import SleekButton from '../../components/ui/NavButtons';

const STEPS = [
  {
    num: "01",
    time: "2 minutes",
    title: "Connect your financial life",
    body: "Link your bank accounts via Plaid — or enter your figures manually. Add your investment properties: address, purchase price, monthly rent, and expenses. That's it.",
    outcome: "Your net worth calculates instantly. No waiting.",
    screen: [
      { label: "Net Worth", value: "$46.5M", color: "dark:text-blue-400 text-blue-600" },
      { label: "Buying Power", value: "$143K", color: "dark:text-blue-300 text-blue-500" },
      { label: "DTI Ratio", value: "34.0%", color: "dark:text-blue-400 text-blue-600" },
    ],
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
      </svg>
    ),
  },
  {
    num: "02",
    time: "Instant",
    title: "See numbers you can actually act on",
    body: "VaWeNa calculates your Deal Readiness Score, DSCR per property, cap rate, cash-on-cash return, and portfolio performance — automatically. No formulas, no spreadsheets.",
    outcome: "You know your financial position before any lender does.",
    screen: [
      { label: "Deal Readiness", value: "76/100", color: "text-emerald-400" },
      { label: "Portfolio DSCR", value: "1.42x", color: "dark:text-blue-400 text-blue-600" },
      { label: "Cash-on-Cash", value: "8.3%", color: "dark:text-blue-300 text-blue-500" },
    ],
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    num: "03",
    time: "When you need it",
    title: "Walk into any meeting prepared",
    body: "Generate a lender-ready DSCR report in one click. Export your Schedule E for your CPA. Get AI alerts when a property underperforms or your buying power shifts.",
    outcome: "No more scrambling. No more guessing. Just confident decisions.",
    screen: [
      { label: "DSCR Report", value: "PDF ready", color: "dark:text-blue-400 text-blue-600" },
      { label: "Schedule E", value: "1-click export", color: "text-emerald-400" },
      { label: "AI Alerts", value: "Real-time", color: "dark:text-blue-300 text-blue-500" },
    ],
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
];

export const ProcessSection: React.FC = () => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [isDesktop, setIsDesktop] = React.useState(false);
  const stepRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  React.useEffect(() => {
    const media = window.matchMedia("(min-width: 1024px)");
    const updateScreen = () => setIsDesktop(media.matches);
    updateScreen();
    media.addEventListener("change", updateScreen);
    return () => media.removeEventListener("change", updateScreen);
  }, []);

  React.useEffect(() => {
    if (!isDesktop) return;

    const interval = window.setInterval(() => {
      setActiveStep((prev) => (prev + 1) % STEPS.length);
    }, 3500);

    return () => window.clearInterval(interval);
  }, [isDesktop]);

  const focusStep = (index: number) => {
    setActiveStep(index);
    stepRefs.current[index]?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <section className="relative w-full px-4 sm:px-6 py-20 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 dark:bg-[#020D30]/60 bg-blue-50/40 pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent dark:via-blue-500/20 via-blue-400/30 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent dark:via-blue-500/20 via-blue-400/30 to-transparent" />

      <div className="max-w-[1100px] mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex px-3 py-1.5 text-xs font-bold tracking-widest dark:text-blue-400 text-blue-700 uppercase dark:bg-blue-600/15 bg-blue-100 rounded-full mb-5">
            How it works
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold dark:text-white text-gray-900 mb-4 leading-tight">
            From zero to financial clarity{" "}
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">in under 10 minutes.</span>
          </h2>
          <p className="text-base dark:text-slate-400 text-gray-500 max-w-lg mx-auto">
            Three steps. No training required. We've seen investors complete setup during a coffee break.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* External clickable step count */}
          <div className="relative mb-6 hidden lg:block">
            <div className="absolute left-[calc(16.67%+1.5rem)] right-[calc(16.67%+1.5rem)] top-1/2 -translate-y-1/2 h-px dark:bg-gradient-to-r dark:from-blue-500/20 dark:via-blue-400/30 dark:to-blue-500/20 bg-gradient-to-r from-blue-200 via-blue-300 to-blue-200 hidden lg:block" />
            <div className="grid grid-cols-3 gap-4 sm:gap-8 relative z-10">
              {STEPS.map((step, i) => (
                <div key={`step-nav-${step.num}`} className="flex justify-center">
                  <motion.button
                    type="button"
                    onClick={() => focusStep(i)}
                    animate={isDesktop && activeStep === i ? { scale: [1, 1.08, 1] } : { scale: 1 }}
                    transition={{ duration: 1.2, repeat: isDesktop && activeStep === i ? Infinity : 0, ease: "easeInOut" }}
                    className={`h-10 w-10 rounded-full text-sm font-extrabold transition-all duration-300 ${
                      activeStep === i
                        ? "bg-gradient-to-br from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/30 scale-105"
                        : "bg-white text-blue-700 border border-blue-300 hover:border-blue-500 hover:text-blue-800 dark:bg-[#0B1F52] dark:text-blue-300 dark:border-blue-500/30 dark:hover:border-blue-400"
                    }`}
                    aria-label={`Focus step ${step.num}`}
                  >
                    {step.num}
                  </motion.button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 relative z-10">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.num}
                ref={(el) => {
                  stepRefs.current[i] = el;
                }}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                animate={{
                  scale: isDesktop && activeStep === i ? 1.03 : 1,
                }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.15 }}
                onClick={() => focusStep(i)}
                className={`group flex flex-col rounded-2xl border bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-blue-500 hover:shadow-xl hover:shadow-blue-500/15 dark:bg-[#07163F]/70 dark:hover:border-blue-500/35 dark:hover:shadow-blue-500/10 ${
                  activeStep === i
                    ? "z-10 border-blue-500 shadow-2xl shadow-blue-500/25 dark:border-blue-400/50 dark:shadow-blue-500/20"
                    : "border-blue-300/80 dark:border-white/[0.07]"
                }`}
              >
                {/* Step header */}
                <div className="flex items-center gap-4 mb-5">
                  <div className="relative w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-500/10 text-blue-600 dark:text-blue-300 flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105">
                    {step.icon}
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest dark:text-blue-400 text-blue-600">{step.time}</div>
                    <h3 className="text-base font-bold dark:text-white text-gray-900 leading-tight">{step.title}</h3>
                  </div>
                </div>

                {/* Body */}
                <p className="text-sm dark:text-slate-400 text-gray-500 leading-relaxed mb-4">
                  {step.body}
                </p>

                {/* Mini screen preview */}
                <div className="dark:bg-[#0B1F52]/60 bg-white border dark:border-white/[0.07] border-blue-200 rounded-xl p-4 mb-4 transition-colors duration-300 group-hover:border-blue-400 dark:group-hover:border-blue-500/30">
                  <div className="grid grid-cols-3 gap-2">
                    {step.screen.map(m => (
                      <div key={m.label} className="text-center">
                        <div className={`text-base font-extrabold ${m.color} leading-none mb-0.5`}>{m.value}</div>
                        <div className="text-[10px] dark:text-slate-500 text-gray-400 leading-tight">{m.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Outcome */}
                <div className="flex items-start gap-2 mt-auto">
                  <svg className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 14 14" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 7l3.5 3.5 5.5-7" />
                  </svg>
                  <p className="text-xs font-semibold dark:text-slate-300 text-gray-600 leading-snug italic">{step.outcome}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Trust + CTA block */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-16 dark:bg-gradient-to-r dark:from-[#0B1F52]/75 dark:to-[#07163F]/70 bg-white border dark:border-blue-700/30 border-blue-300/80 rounded-2xl px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-blue-500 hover:shadow-xl hover:shadow-blue-500/15 dark:hover:border-blue-500/35 dark:hover:shadow-blue-500/10"
        >
          {/* Trust badges */}
          <div className="flex flex-wrap justify-center md:justify-start gap-5">
            {[
              { icon: "✓", text: "14-day free trial — we'll remind you before you're charged" },
              { icon: "✓", text: "Save 17% with annual billing" },
              { icon: "✓", text: "Cancel anytime" },
            ].map(t => (
              <span key={t.text} className="flex items-center gap-2 text-sm dark:text-slate-300 text-gray-600">
                <span className="text-emerald-400 font-bold">{t.icon}</span>
                {t.text}
              </span>
            ))}
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <SleekButton
              variant="primary"
              className="!px-7 !py-3 !text-sm !whitespace-nowrap"
              onClick={() => window.open('https://app.vawena.ai/signup', '_self')}
            >
              Start Free Trial
            </SleekButton>
            <SleekButton
              variant="light"
              className="!px-7 !py-3 !text-sm !whitespace-nowrap"
              onClick={() => window.open('/contact', '_self')}
            >
              Book a Demo
            </SleekButton>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
